import { RenderStateColorGradientKnot } from "@novorender/api";
import { useEffect } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { VecRGBA } from "utils/color";

import { LabeledKnot } from "..";
import { selectClassificationColorGradient, selectDefaultPointVisualization } from "../renderSlice";

export function useHandlePointVisualization() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const defaultPointVisualization = useAppSelector(selectDefaultPointVisualization);
    const classificationColorGradient = useAppSelector(selectClassificationColorGradient);

    useEffect(() => {
        if (!view) {
            return;
        }

        if (defaultPointVisualization.kind === "classification") {
            view.modifyRenderState({
                highlights: {
                    defaultPointVisualization,
                },
                points: {
                    classificationColorGradient: {
                        knots: prepareClassificationColorGradientKnots(
                            classificationColorGradient.knots,
                            classificationColorGradient.undefinedColor,
                            classificationColorGradient.highlightedPosition,
                        ),
                    },
                    undefinedColor: classificationColorGradient.undefinedColor,
                },
            });
        } else if (defaultPointVisualization.kind === "elevation") {
            view.modifyRenderState({
                highlights: {
                    defaultPointVisualization,
                },
                // terrain.elevationGradient is set in useHandleTerrain
            });
        } else {
            view.modifyRenderState({
                highlights: {
                    defaultPointVisualization,
                },
            });
        }
    }, [view, classificationColorGradient, defaultPointVisualization]);
}

// If classification color is not defined for a value - it's gonna be inter/extrapolated right now,
// but we want it to have undefined color instead.
export function prepareClassificationColorGradientKnots(
    knots: LabeledKnot[],
    undefinedColor: VecRGBA,
    highlightPosition: number | undefined,
) {
    if (highlightPosition !== undefined) {
        // Hide everything else in this case
        undefinedColor = [0, 0, 0, 0];
        knots = knots.map((knot) => ({
            position: knot.position,
            color: highlightPosition === knot.position ? knot.color : undefinedColor,
            label: knot.label,
        }));
    }

    const newKnots: RenderStateColorGradientKnot<VecRGBA>[] = [];
    if (knots.length === 0) {
        return [];
    }

    if (knots[0].position !== 0) {
        newKnots.push({ position: knots[0].position - 1, color: undefinedColor });
    }
    newKnots.push({ position: knots[0].position, color: knots[0].color });

    for (let i = 1; i < knots.length; i++) {
        const prevKnot = knots[i - 1];
        const knot = knots[i];
        if (knot.position !== prevKnot.position + 1) {
            const k1 = prevKnot.position + 1;
            newKnots.push({ position: k1, color: undefinedColor });
            const k2 = knot.position - 1;
            if (k2 !== k1) {
                newKnots.push({ position: k2, color: undefinedColor });
            }
        }
        newKnots.push({ position: knot.position, color: knot.color });
    }

    newKnots.push({ position: knots[knots.length - 1].position + 1, color: undefinedColor });

    return newKnots;
}
